if (!customElements.get("swiper-slider")) {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.slideContainer = this.querySelector("[data-swiper]:not([data-swiper-thumbs])");
      this.slides = this.querySelectorAll("[data-swiper-slide]");
      this.sliderOptions = this.applyDefaultOptions(this.getSliderOptions());
      this.activeIndex = 0;
      this.initialized = false;
      this.resizeBound = false;
      this.asyncObserver = null;
      /**
       * Tracks the last non-zero computed slide height in pixels so we never set
       * the CSS variable to 0 when the slider is measured while hidden.
       * @type {number}
       */
      this.lastKnownSlideHeightPx = 0;
      if (this.dataset.swiperThumbs) {
        this.thumbnails = this.querySelector('[data-swiper-thumbs]');
      }
      this.boundInitializeSwiper = this.initializeSwiper.bind(this);
      this.boundSlideToIndex = this.slideToIndex.bind(this);
      this.boundUpdateMaxSlideHeight = this.updateMaxSlideHeight.bind(this);
      this.boundOnReinitialize = this.onReinitialize.bind(this);
      this.boundCheckForSlides = this.checkForSlides.bind(this);
    }

    connectedCallback() {
        if (this.dataset.customEvent) {
          this.attachCustomListeners(this.dataset.customEvent);
        }
        if (this.slideContainer && this.slides.length > 0) {
          this.initializeSwiper()
        } else if (this.dataset.asyncLoaded) {
          // Wait for async content (e.g., Vue.js rendering)
          this.waitForAsyncContent();
        } else {
          console.warn("SwiperSlider - Slider or Slides Not Found");
        }
    }

    /**
     * Waits for async content to be loaded (e.g., Vue.js rendering)
     * Uses MutationObserver to detect when slides are added to the DOM
     */
    waitForAsyncContent() {
      // Check immediately in case content is already loaded
      this.checkForSlides();
      
      // Set up MutationObserver to watch for slides being added
      this.asyncObserver = new MutationObserver(() => {
        this.checkForSlides();
      });
      
      // Observe the entire component for child additions
      this.asyncObserver.observe(this, {
        childList: true,
        subtree: true
      });
      
      // Fallback timeout to stop observing after 10 seconds
      setTimeout(() => {
        if (this.asyncObserver && !this.initialized) {
          this.asyncObserver.disconnect();
          this.asyncObserver = null;
          if (!this.initialized) {
            console.warn("SwiperSlider - Timeout waiting for async content");
          }
        }
      }, 10000);
    }

    /**
     * Checks if slides are now available and initializes if so
     */
    checkForSlides() {
      // Re-query for slides in case they've been added
      this.slides = this.querySelectorAll("[data-swiper-slide]");
      this.slideContainer = this.querySelector("[data-swiper]:not([data-swiper-thumbs])");
      
      if (this.slideContainer && this.slides.length > 0 && !this.initialized) {
        this.sliderOptions = this.applyDefaultOptions(this.getSliderOptions());
        if (this.asyncObserver) {
          this.asyncObserver.disconnect();
          this.asyncObserver = null;
        }
        this.initializeSwiper();
      }
    }

    /**
     * @param {string} customEvent
     */
    attachCustomListeners(customEvent) {
      switch (customEvent) {
        case "reinitialize":
          // Recompute sizes and heights when the component becomes visible
          this.addEventListener('reinitialize-swiper', this.boundOnReinitialize);
          break;
        case "slide-to-index":
          document.addEventListener('slide-to-index', this.boundSlideToIndex);
          break;
      }
    }
    
    disconnectedCallback() {
      window.removeEventListener("resize", this.handleResize);
      window.removeEventListener("resize", this.boundUpdateMaxSlideHeight);
      if (this.asyncObserver) {
        this.asyncObserver.disconnect();
        this.asyncObserver = null;
      }
      if (this.dataset.customEvent) {
        switch (this.dataset.customEvent) {
          case "reinitialize":
            this.removeEventListener('reinitialize-swiper', this.boundOnReinitialize);
            break;
          case "slide-to-index":
            document.removeEventListener('slide-to-index', this.boundSlideToIndex);
            break;
        }
      }
    }

    /**
     * @param {any} options
     */
    applyDefaultOptions(options) {

      if (options.loop === undefined) {
        options.loop = true;
      }
      if (options.speed === undefined) {
        options.speed = 400;
      }
      return options;
    }

    getSliderOptions() {
      try {

        return JSON.parse(this.dataset.swiperOptions || '{}');
      } catch (err) {
        //console.log(err);
        return {};
      }
    }

    initializeSwiper() {
      console.log('initializeSwiper', this.initialized);
      if (window.Swiper && !this.initialized) {
        this.initialized = true;
        if(this.thumbnails) {
          this.setThumbnails()
        }
        if (this.dataset.hasOwnProperty('fractionPagination')) {
          this.sliderOptions.on = {
            slideChange:  this.updateFractions.bind(this),
          };
        }
        this.slider = new Swiper(this.slideContainer, this.sliderOptions);
        this.initializeSliderEventListeners();
        this.bindImageLoadEvents();
        // Defer height calculation until after Swiper completes initial layout
        this.deferUpdateMaxSlideHeight();
        // Listen to Swiper lifecycle/layout events that can affect slide heights
        try {
          this.slider.on('init', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('afterInit', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('resize', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('update', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('breakpoint', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('observerUpdate', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('setTranslate', this.deferUpdateMaxSlideHeight.bind(this));
          this.slider.on('imagesReady', this.boundUpdateMaxSlideHeight);
        } catch (e) {
          // Swiper event binding failed; fall back to a delayed measure
          setTimeout(this.boundUpdateMaxSlideHeight, 50);
        }
        if (this.dataset.hasOwnProperty('fractionPagination')) {
          this.updateFractions();
        }
      } else if (!window.Swiper) {
        console.warn("SwiperSlider - Swiper.js not found");
      }
      this.bindResizeEvent();
    }

    /**
     * Handle external requests to reinitialize sizing/measurement.
     * This is triggered when parent visibility changes (e.g., tab/panel activation).
     */
    onReinitialize() {
      // If Swiper is already initialized, update its size then remeasure heights.
      if (this.slider && this.initialized) {
        try {
          this.slider.updateSize();
          this.slider.update();
        } catch (e) {
          // Swiper might not support update in some scenarios; ignore.
        }
        // Double rAF to wait for layout to settle after panel becomes visible
        this.deferUpdateMaxSlideHeight();
        if (this.dataset.hasOwnProperty('fractionPagination')) {
          this.updateFractions();
        }
      } else {
        // Not initialized yet; attempt to initialize now that it may be visible
        this.initializeSwiper();
      }
    }

    bindResizeEvent() {
      if (!this.resizeBound) {
        window.addEventListener("resize", this.handleResize.bind(this));
        window.addEventListener("resize", this.updateFractions.bind(this));
        window.addEventListener("resize", this.boundUpdateMaxSlideHeight);
        this.resizeBound = true;
      }
    }

    /**
     * @param {Event} e
     */
    handleResize(e) {
      if (this.slider) {
        setTimeout(() => {
          this.slider.updateSize();
          // Defer to ensure slides widths are finalized before measuring height
          this.deferUpdateMaxSlideHeight();
        }, 300);
      }
    }
    updateFractions() {
      const currentContainer = this.parentElement?.querySelector('.td-swiper-pagination-current')
      const totalContainer = this.parentElement?.querySelector('.td-swiper-pagination-total')
      if (currentContainer && totalContainer && this.slider) {
        const current = (this.slider.realIndex || 0) + 1;
        // Prefer pagination bullets length if available, otherwise fall back to snapGrid length or slides length
        const total = (this.slider.pagination && this.slider.pagination.bullets && this.slider.pagination.bullets.length)
          ? this.slider.pagination.bullets.length
          : (Array.isArray(this.slider.snapGrid) && this.slider.snapGrid.length > 0
              ? this.slider.snapGrid.length
              : this.slider.slides?.length || current);
        currentContainer.innerHTML = `${current}`.padStart(2, '0');
        totalContainer.innerHTML = `${total}`.padStart(2, '0');
      }
    }

    setThumbnails() {
      /** @type {HTMLElement} */
      const thumbsSlideContainer = /** @type {any} */ (this.thumbnails);
      const thumbOptions = JSON.parse((thumbsSlideContainer?.dataset?.swiperOptions) || '{}');
      const thumbnailsSlider = new Swiper(thumbsSlideContainer, thumbOptions);
      this.sliderOptions.thumbs = {swiper: thumbnailsSlider};
      this.sliderOptions.on = {
      	slideChange: function () {
          let activeIndex = this.activeIndex;
          document.dispatchEvent(new CustomEvent('thumbnail-changed', {
            detail: {
              slide: this.slides[this.activeIndex]
            }
          }))
          this.thumbs.swiper.slideTo(activeIndex > 0 ? activeIndex - 1 : 0);    
        }
      }
    }

    initializeSliderEventListeners() {}

    /**
     * Schedules a height recalculation after layout is stable.
     * Uses double rAF to wait for Swiper's internal measurements/styles to apply.
     */
    deferUpdateMaxSlideHeight() {
      requestAnimationFrame(() => {
        requestAnimationFrame(this.boundUpdateMaxSlideHeight);
      });
    }

    /**
     * Calculates the tallest slide height and sets it as a CSS variable on the host element
     * Variable: --td-swiper-max-slide-height
     */
    updateMaxSlideHeight() {
      // Avoid measuring when slider is not visible; do not set height to 0.
      if (!this.slides || this.slides.length === 0) return;
      if (!this.isElementVisible(this)) return;

      let tallest = 0;
      this.slides.forEach((slide) => {
        /** @type {HTMLElement} */
        const el = /** @type {any} */ (slide);
        const rect = el.getBoundingClientRect();
        const height = rect.height || el.offsetHeight || 0;
        if (height > tallest) tallest = height;
      });

      // Fail-safe: never set the CSS var to 0. Keep previous non-zero value if available.
      if (tallest <= 0) {
        if (this.lastKnownSlideHeightPx > 0) {
          // Preserve prior value and attempt a delayed remeasure
          this.style.setProperty('--td-swiper-max-slide-height', `${Math.round(this.lastKnownSlideHeightPx)}px`);
        }
        // Retry soon in case images/layout settle
        setTimeout(this.boundUpdateMaxSlideHeight, 50);
        return;
      }

      this.lastKnownSlideHeightPx = Math.round(tallest);
      this.style.setProperty('--td-swiper-max-slide-height', `${this.lastKnownSlideHeightPx}px`);
    }

    /**
     * Checks if an element is visible for measurement purposes.
     * @param {Element} el
     */
    isElementVisible(el) {
      // Offset parent is null when display:none (or detached). Also guard for zero width.
      const host = /** @type {HTMLElement} */ (el);
      if (!host) return false;
      if (host.offsetParent === null) return false;
      const rect = host.getBoundingClientRect();
      return rect.width > 0 && rect.height >= 0; // allow height 0 width >0 during initial layout
    }

    /**
     * Recalculate tallest height once images inside slides load
     */
    bindImageLoadEvents() {
      const images = this.querySelectorAll('[data-swiper-slide] img');
      images.forEach((img) => {
        const imageEl = /** @type {HTMLImageElement} */ (img);
        if (imageEl.complete) return;
        imageEl.addEventListener('load', this.boundUpdateMaxSlideHeight, { once: true });
        imageEl.addEventListener('error', this.boundUpdateMaxSlideHeight, { once: true });
      });
    }

    /**
     * Slides to a specific index when receiving a document event
     * @param {CustomEvent} event - The custom event containing slide index details
     */
    slideToIndex(event) {
      if (!this.slider || !this.initialized) {
        console.warn("SwiperSlider - Slider not initialized");
        return;
      }

      const { index, speed, sliderId } = event.detail || {};
      
      // If sliderId is specified, only respond to events meant for this slider
      if (sliderId && this.dataset.sliderId !== sliderId) {
        return;
      }

      if (typeof index === 'number' && index >= 0 && index <= this.slides.length) {
        let slidesIndex = undefined
        for ( let i = 0; i < this.slider.slides.length; i++ ) {
          if (parseInt(this.slider.slides[i].dataset.actualSlideIndex) === index) {
            slidesIndex = i;
          }
        }
        const slideSpeed = speed !== undefined ? speed : this.sliderOptions.speed;
        this.slider.slideTo(slidesIndex || index, slideSpeed);
      } else {
        console.warn("SwiperSlider - Invalid slide index:", index);
      }
    }
  }
  customElements.define("swiper-slider", SwiperSlider);

}